// src/pages/CRM/SFA/SalesTeams.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel,
  useTheme,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  RemoveCircle as RemoveCircleIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { salesTeamService } from './sfaService';

// Role labels and colors
const ROLE_CONFIG = {
  MANAGER: { label: 'Manager', color: 'primary', icon: <SupervisorAccountIcon /> },
  TEAM_LEAD: { label: 'Team Lead', color: 'secondary', icon: <AdminIcon /> },
  SALES_REP: { label: 'Sales Rep', color: 'info', icon: <PersonIcon /> },
  MEMBER: { label: 'Member', color: 'default', icon: <PersonIcon /> }
};

const SalesTeams = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    is_active: ''
  });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  
  const [editingTeam, setEditingTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manager_id: null,
    is_active: true
  });

  // Add member form
  const [memberFormData, setMemberFormData] = useState({
    user_id: null,
    role: 'MEMBER'
  });

  // Form validation
  const [formErrors, setFormErrors] = useState({});

  // Permissions
  const [canView, setCanView] = useState(true);
  const [canAdd, setCanAdd] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Summary stats
  const [summary, setSummary] = useState({
    total_teams: 0,
    active_teams: 0,
    total_members: 0
  });

  // Check permissions and current user
  useEffect(() => {
    const checkPerms = async () => {
      try {
        const viewPerm = await hasPermission('crm', 'view');
        const addPerm = await hasPermission('crm', 'add');
        const editPerm = await hasPermission('crm', 'edit');
        setCanView(viewPerm);
        setCanAdd(addPerm);
        setCanEdit(editPerm);
        
        console.log('Permissions - View:', viewPerm, 'Add:', addPerm, 'Edit:', editPerm);
      } catch (err) {
        console.error('Error checking permissions:', err);
        setCanView(true);
        setCanAdd(true);
        setCanEdit(true);
      }
    };
    checkPerms();
    
    // Get current user info
    try {
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(userInfo);
      
      const userId = userInfo.user_id;
      const teamOwnerId = userInfo.active_team?.team_id; // team_id is the team_owner_id
      
      // User is admin if their user_id matches the team_id (team owner)
      const adminCheck = userId && teamOwnerId && (String(userId) === String(teamOwnerId));
      
      console.log('=== ADMIN CHECK DEBUG ===');
      console.log('User ID:', userId);
      console.log('Team Owner ID (active_team.team_id):', teamOwnerId);
      console.log('Is Admin:', adminCheck);
      console.log('=======================');
      
      setIsAdmin(adminCheck);
    } catch (err) {
      console.error('Error parsing user info:', err);
      setIsAdmin(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    loadTeams();
  }, [page, rowsPerPage]);

  // Load teams
  const loadTeams = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        include_members: false
      };

      if (filters.is_active !== '') {
        params.is_active = filters.is_active;
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await salesTeamService.getAll(params);
      
      const teamsData = response.data || response.teams || [];
      setTeams(teamsData);
      setTotalCount(response.total || response.pagination?.total || teamsData.length);

      // Calculate summary
      setSummary({
        total_teams: response.total || teamsData.length,
        active_teams: teamsData.filter(t => t.is_active).length,
        total_members: teamsData.reduce((sum, t) => sum + (t.member_count || 0), 0)
      });
    } catch (error) {
      console.error('Error loading teams:', error);
      enqueueSnackbar('Failed to load teams', { variant: 'error' });
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  // Load available managers
  useEffect(() => {
    loadAvailableManagers();
  }, []);

  const loadAvailableManagers = async () => {
    try {
      const response = await salesTeamService.getAvailableManagers();
      setAvailableManagers(response.data || []);
    } catch (error) {
      console.error('Error loading available managers:', error);
      setAvailableManagers([]);
    }
  };

  // Update permission check for create button
  const canCreateTeam = () => {
      // Admin can always create
      if (isAdmin) return true;
      
      // Check if user is a manager of any team
      const isManagedByUser = teams.some(t => t.manager_id === currentUser?.user_id && t.is_active);
      return isManagedByUser;
  };

  

  // Load team members
  const loadTeamMembers = async (teamId) => {
    try {
      const response = await salesTeamService.getMembers(teamId);
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      enqueueSnackbar('Failed to load team members', { variant: 'error' });
      setTeamMembers([]);
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTeams();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search, filters.is_active]);

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Team name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Team name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Team name must be less than 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open create dialog
  const openCreateDialog = () => {
    setEditingTeam(null);
    setFormData({
      name: '',
      description: '',
      manager_id: null,
      is_active: true
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name || '',
      description: team.description || '',
      manager_id: team.manager_id || null,
      is_active: team.is_active !== false
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  // Open members dialog
  const openMembersDialog = async (team) => {
    setSelectedTeam(team);
    await loadTeamMembers(team.id);
    setMembersDialogOpen(true);
  };

  // Open add member dialog
  const openAddMemberDialog = () => {
    setMemberFormData({
      user_id: null,
      role: 'MEMBER'
    });
    setAddMemberDialogOpen(true);
  };

  // Save team
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        manager_id: formData.manager_id,
        is_active: formData.is_active
      };

      if (editingTeam) {
        await salesTeamService.update(editingTeam.id, payload);
        enqueueSnackbar('Team updated successfully', { variant: 'success' });
      } else {
        await salesTeamService.create(payload);
        enqueueSnackbar('Team created successfully', { variant: 'success' });
      }

      setDialogOpen(false);
      loadTeams();
    } catch (error) {
      console.error('Error saving team:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save team';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      
      if (errorMessage.toLowerCase().includes('already exists')) {
        setFormErrors(prev => ({ ...prev, name: 'A team with this name already exists' }));
      }
    }
  };

  // Delete team
  const handleDelete = async () => {
    if (!deletingTeam) return;

    try {
      await salesTeamService.delete(deletingTeam.id);
      enqueueSnackbar('Team deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingTeam(null);
      loadTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to delete team', { variant: 'error' });
    }
  };

  // Add member to team
  const handleAddMember = async () => {
    if (!memberFormData.user_id) {
      enqueueSnackbar('Please select a user', { variant: 'warning' });
      return;
    }

    try {
      await salesTeamService.addMember(selectedTeam.id, memberFormData);
      enqueueSnackbar('Member added successfully', { variant: 'success' });
      setAddMemberDialogOpen(false);
      loadTeamMembers(selectedTeam.id);
      loadTeams(); // Refresh counts
    } catch (error) {
      console.error('Error adding member:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to add member', { variant: 'error' });
    }
  };

  // Update member role
  const handleUpdateMemberRole = async (member, newRole) => {
    try {
      await salesTeamService.updateMember(selectedTeam.id, member.id, { role: newRole });
      enqueueSnackbar('Member role updated', { variant: 'success' });
      loadTeamMembers(selectedTeam.id);
      loadTeams();
    } catch (error) {
      console.error('Error updating member role:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to update member', { variant: 'error' });
    }
  };

  // Remove member
  const handleRemoveMember = async (member) => {
    if (member.role === 'MANAGER') {
      enqueueSnackbar('Cannot remove manager. Assign a new manager first.', { variant: 'warning' });
      return;
    }

    try {
      await salesTeamService.removeMember(selectedTeam.id, member.id);
      enqueueSnackbar('Member removed successfully', { variant: 'success' });
      loadTeamMembers(selectedTeam.id);
      loadTeams();
    } catch (error) {
      console.error('Error removing member:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to remove member', { variant: 'error' });
    }
  };

  // Check if current user can manage the team
  const canManageTeam = (team) => {
    if (!team) return false;
    if (isAdmin) return true;
    
    const userId = currentUser?.user_id || currentUser?.userId || currentUser?.id;
    return team.manager_id === userId;
  };

  // Get user display name
  const getUserName = (user) => {
    if (!user) return 'Unknown';
    const firstName = user.first_name || user.firstName || user.fname || '';
    const lastName = user.last_name || user.lastName || user.lname || '';
    const name = `${firstName} ${lastName}`.trim();
    return name || user.email || user.user_email || 'Unknown';
  };

  // Get manager display
  const getManagerDisplay = (team) => {
    if (team.manager) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
            {team.manager.name?.charAt(0).toUpperCase() || 'M'}
          </Avatar>
          <Typography variant="body2">{team.manager.name}</Typography>
        </Box>
      );
    }
    
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No manager assigned
      </Typography>
    );
  };

  // Get available users for adding (exclude existing members)
  const getAvailableUsers = () => {
    const existingUserIds = teamMembers.map(m => m.user_id);
    // Use availableManagers instead of users - they come from team_memberships
    return availableManagers.filter(u => !existingUserIds.includes(u.user_id));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view sales teams.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <GroupsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Sales Teams
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage sales teams, assign managers, and organize team members
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTeams}
            disabled={loading}
          >
            Refresh
          </Button>
          {/* Show button if user has add permission OR is admin */}
          {(canAdd || canCreateTeam()) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Create Team
            </Button>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Teams</Typography>
                  <Typography variant="h4" fontWeight={700}>{summary.total_teams}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <GroupsIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Teams</Typography>
                  <Typography variant="h4" fontWeight={700}>{summary.active_teams}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <CheckCircleIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Members</Typography>
                  <Typography variant="h4" fontWeight={700}>{summary.total_members}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search teams..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_active}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="text"
              onClick={() => setFilters({ search: '', is_active: '' })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Teams Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading && <LinearProgress />}

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Team Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Manager</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Members</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <GroupsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary" gutterBottom>
                      No sales teams found
                    </Typography>
                    {/* Fixed: Show button if user has add permission OR is admin */}
                    {(canAdd || isAdmin) && (
                      <Button
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{ mt: 2 }}
                      >
                        Create First Team
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                teams.map((team) => (
                  <TableRow key={team.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          variant="rounded"
                          sx={{ 
                            bgcolor: team.is_active ? 'primary.light' : 'grey.300', 
                            width: 40, 
                            height: 40 
                          }}
                        >
                          <GroupIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {team.name}
                          </Typography>
                          {team.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              display: 'block',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {team.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getManagerDisplay(team)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<PersonIcon />}
                        label={`${team.member_count || 0} members`}
                        size="small"
                        variant="outlined"
                        onClick={() => openMembersDialog(team)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={team.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={team.is_active ? 'Active' : 'Inactive'}
                        color={team.is_active ? 'success' : 'default'}
                        variant={team.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(team.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Members">
                          <IconButton
                            size="small"
                            onClick={() => openMembersDialog(team)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Fixed: Show edit/delete if user can edit OR is admin */}
                        {(canEdit || isAdmin) && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(team)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(canEdit || isAdmin) && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeletingTeam(team);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create/Edit Team Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" />
            {editingTeam ? 'Edit Sales Team' : 'Create Sales Team'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                }}
                required
                error={!!formErrors.name}
                helperText={formErrors.name || 'Enter a unique name for this team'}
                placeholder="e.g., East Region Sales"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                error={!!formErrors.description}
                helperText={formErrors.description}
                placeholder="Brief description of the team"
                multiline
                rows={2}
              />
            </Grid>            

            <Grid item xs={12}>
              <Autocomplete
                options={availableManagers}
                getOptionLabel={(opt) => opt.name}
                value={availableManagers.find(u => u.user_id === formData.manager_id) || null}
                onChange={(e, manager) => setFormData(prev => ({
                  ...prev,
                  manager_id: manager?.user_id || null
                }))}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                      {option.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Team Manager" 
                    placeholder="Select a manager from your team"
                    helperText="Select from team members who can manage this team"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.is_active 
                        ? 'Team is active and visible' 
                        : 'Team is inactive and hidden from selections'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingTeam ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon color="primary" />
              {selectedTeam?.name} - Members
            </Box>
            {canManageTeam(selectedTeam) && (
              <Button
                startIcon={<PersonAddIcon />}
                size="small"
                onClick={openAddMemberDialog}
              >
                Add Member
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {teamMembers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography color="text.secondary">No members yet</Typography>
              {canManageTeam(selectedTeam) && (
                <Button
                  startIcon={<PersonAddIcon />}
                  onClick={openAddMemberDialog}
                  sx={{ mt: 2 }}
                >
                  Add First Member
                </Button>
              )}
            </Box>
          ) : (
            <List>
              {teamMembers.map((member, index) => (
                <React.Fragment key={member.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: ROLE_CONFIG[member.role]?.color === 'primary' ? 'primary.main' : 'grey.400' }}>
                        {member.user_name?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.user_name || member.user_email || 'Unknown User'}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={ROLE_CONFIG[member.role]?.label || member.role}
                            color={ROLE_CONFIG[member.role]?.color || 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Joined {formatDate(member.joined_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    {canManageTeam(selectedTeam) && member.role !== 'MANAGER' && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove Member">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member)}
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={addMemberDialogOpen}
        onClose={() => setAddMemberDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon color="primary" />
            Add Team Member
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={getAvailableUsers()}
                getOptionLabel={(opt) => opt.name} // Changed from getUserName(opt)
                value={getAvailableUsers().find(u => u.user_id === memberFormData.user_id) || null}
                onChange={(e, user) => setMemberFormData(prev => ({
                  ...prev,
                  user_id: user?.user_id || null
                }))}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                      {option.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" required placeholder="Select a team member" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={memberFormData.role}
                  label="Role"
                  onChange={(e) => setMemberFormData(prev => ({ ...prev, role: e.target.value }))}
                >
                  <MenuItem value="MEMBER">Member</MenuItem>
                  <MenuItem value="SALES_REP">Sales Rep</MenuItem>
                  <MenuItem value="TEAM_LEAD">Team Lead</MenuItem>
                  {isAdmin && <MenuItem value="MANAGER">Manager</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember}>
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon />
            Delete Sales Team
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete the team <strong>"{deletingTeam?.name}"</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 1 }}>
            <Typography variant="body2">
              This will remove all team members. If this team is assigned to any sales targets, 
              it will be deactivated instead of deleted.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesTeams;